/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { PayerTypeEnum, AccountingMovementInterface } from "@malmur-technologies/medicalsystem-shared";

/**
 * Rounds a number to two decimal places for currency precision.
 * @param num - The number to round
 * @returns The number rounded to 2 decimal places
 */
export const round2 = (num: number) => Math.round(num * 100) / 100;

/**
 * Calculates accounting totals from movement records including billing and payment breakdowns.
 * Separates insurance and patient portions based on claim status and co-pay/co-insurance.
 * Uses the amountDue from the most recent movement as the current balance.
 * @param movements - Array of accounting movement records containing billing and payment data (ordered by date DESC)
 * @returns Object with detailed financial totals including billing, payments, and balances for insurance and patient
 */
export function calculateAccountingTotals(movements: AccountingMovementInterface[]) {
  let totalBilling = 0;
  let totalPayment = 0;

  let totalInsurance = 0;
  let totalPatient = 0;

  let totalPaymentInsurance = 0;
  let totalPaymentPatient = 0;

  movements.forEach((movement) => {
    // Billing
    if (movement.billingEncounter) {
      const billingEncounter = movement.billingEncounter;
      const price = billingEncounter.price ?? 0;
      totalBilling += price;
      if (billingEncounter.isClaim) {
        const coPay: number =
          billingEncounter.appointment?.overrideCopay ??
          billingEncounter.appointment?.patientInsurance?.coPay ??
          0;

        const coInsurancePercent: number =
          billingEncounter.appointment?.patientInsurance?.coInsurance ?? 0;

        const coInsuranceAmount: number = (price - coPay) * (coInsurancePercent / 100);

        const insurancePortion = price - coPay - coInsuranceAmount;
        const patientPortion = coPay + coInsuranceAmount;
        totalInsurance += insurancePortion;
        totalPatient += patientPortion;
      } else {
        totalPatient += price;
      }
    }

    // Payments
    if (movement.payment) {
      const posted = movement.payment?.posted ?? 0;
      totalPayment += posted;

      if (movement.payment?.payerType === PayerTypeEnum.INSURANCE) {
        totalPaymentInsurance += posted;
      } else if (
        [PayerTypeEnum.PATIENT, PayerTypeEnum.THIRD_PARTY].includes(movement.payment?.payerType)
      ) {
        totalPaymentPatient += posted;
      }
    }
  });

  totalBilling = round2(totalBilling);
  totalPayment = round2(totalPayment);
  totalInsurance = round2(totalInsurance);
  totalPatient = round2(totalPatient);
  totalPaymentInsurance = round2(totalPaymentInsurance);
  totalPaymentPatient = round2(totalPaymentPatient);

  // Use the amountDue from the most recent movement (first in DESC order) as the actual balance
  const currentBalance = movements.length > 0 ? round2(movements[0].amountDue ?? 0) : 0;

  const balance = currentBalance;
  const balanceInsurance = round2(totalInsurance - totalPaymentInsurance);
  const balancePatient = currentBalance; // The amountDue represents the patient balance

  return {
    totalBilling,
    totalPayment,
    balance,
    totalInsurance,
    totalPatient,
    totalPaymentInsurance,
    totalPaymentPatient,
    balanceInsurance,
    balancePatient,
  };
}

